import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { MarketplaceSellerSelectorComponent } from '../../shared/design-system/marketplace-seller-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import {
  CatalogService,
  MarketplaceSellerTierBandRecord,
  MarketplaceSellerTierProfileRecord
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-marketplace-seller-tiers',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    MarketplaceSellerSelectorComponent
  ],
  template: `
    <ain-page
      [title]="t('catalog.marketplace.sellerTiers.title')"
      [description]="t('catalog.marketplace.sellerTiers.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.sellerTiers.help.title'"
        [descriptionKey]="'catalog.marketplace.sellerTiers.help.description'"
        [bulletKeys]="[
          'catalog.marketplace.sellerTiers.help.bullets.scoring',
          'catalog.marketplace.sellerTiers.help.bullets.override'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <div class="toolbar mb-3">
          <button pButton type="button" [label]="t('catalog.marketplace.sellerTiers.runScoring')" (click)="runScoring()"></button>
        </div>
        <p-table [value]="bands()" [tableStyle]="{ 'min-width': '40rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.tier') }}</th>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.minScore') }}</th>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.commissionDiscount') }}</th>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.buyBox') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.displayName }} ({{ row.tierCode }})</td>
              <td>{{ row.minScore }}</td>
              <td>{{ row.commissionDiscountPercent }}%</td>
              <td>{{ row.buyBoxScoreAdjustment }}</td>
            </tr>
          </ng-template>
        </p-table>
        <p-table class="mt-4" [value]="profiles()" [tableStyle]="{ 'min-width': '48rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.seller') }}</th>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.score') }}</th>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.effectiveTier') }}</th>
              <th>{{ t('catalog.marketplace.sellerTiers.columns.override') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.sellerId }}</td>
              <td>{{ row.computedScore }}</td>
              <td>{{ row.effectiveTierCode }}</td>
              <td>{{ row.overrideTierCode ?? '—' }}</td>
            </tr>
          </ng-template>
        </p-table>
        <section class="override-panel mt-4">
          <h3>{{ t('catalog.marketplace.sellerTiers.overrideTitle') }}</h3>
          <ain-marketplace-seller-selector [(ngModel)]="overrideSellerId" />
          <input pInputText class="w-full mt-2" [(ngModel)]="overrideTierCode" [placeholder]="t('catalog.marketplace.sellerTiers.overrideTierPlaceholder')" />
          <textarea pTextarea class="w-full mt-2" rows="2" [(ngModel)]="overrideReason" [placeholder]="t('catalog.marketplace.sellerTiers.overrideReasonPlaceholder')"></textarea>
          <button pButton type="button" class="mt-2" [label]="t('catalog.marketplace.sellerTiers.applyOverride')" (click)="applyOverride()"></button>
        </section>
      }
    </ain-page>
  `
})
export class MarketplaceSellerTiersComponent implements OnInit {
  private readonly api = inject(CatalogService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly bands = signal<MarketplaceSellerTierBandRecord[]>([]);
  readonly profiles = signal<MarketplaceSellerTierProfileRecord[]>([]);
  overrideSellerId: string | null = null;
  overrideTierCode = '';
  overrideReason = '';

  ngOnInit(): void {
    this.reload();
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  reload(): void {
    this.loading.set(true);
    this.api.listMarketplaceSellerTierBands().subscribe({
      next: bands => this.bands.set(bands ?? []),
      error: () => this.bands.set([])
    });
    this.api.listMarketplaceSellerTierProfiles().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: profiles => this.profiles.set(profiles ?? []),
      error: () => this.profiles.set([])
    });
  }

  runScoring(): void {
    this.api.runMarketplaceSellerTierScoring().subscribe(() => this.reload());
  }

  applyOverride(): void {
    if (!this.overrideSellerId) return;
    this.api.overrideMarketplaceSellerTier(this.overrideSellerId, {
      tierCode: this.overrideTierCode.trim() || null,
      reason: this.overrideReason.trim() || null,
      actorReference: null,
      untilUtc: null
    }).subscribe(() => this.reload());
  }
}
